import { format } from "date-fns/format";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { Card, CardContent, CardHeader } from "@/components/atomic/Card";
import BarChart from "@/components/ui/bi/BarChart";
import ComparisonChart from "@/components/ui/bi/ComparisonChart";
import KumiaiCategoryRanking from "@/components/ui/bi/KumiaiCategoryRanking";
import KumiaiComparisonChart from "@/components/ui/bi/KumiaiComparisonChart";
import useConfig from "@/hooks/useConfig";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { DisplayGroup } from "@/models/displayGroup";
import { VocCountData } from "@/models/vocCount";
import BITableContainer from "./BITableContainer";

interface Props {
  className?: string;
  displayGroup: DisplayGroup;
  vocCountData: VocCountData;
  hideTable?: boolean;
  hideDataVisibilityToggle?: boolean; // 「データを開く/閉じる」ボタンを非表示にするかどうか
}

const BIContainer: React.FC<Props> = (
  {
    className = "",
    displayGroup,
    vocCountData,
    hideTable = false,
    hideDataVisibilityToggle = false,
  },
) => {
  const { t } = useTranslation("voiceek_ui");
  const { config } = useConfig();
  const { currentWorkspace } = useWorkspaces();

  // 年次表示を制御（Config APIから取得、デフォルトは月次）
  const isYearlyView = config?.isYearlyView ?? false;

  // VoE環境で質問項目名が設定されている場合に表示
  const shouldShowQuestionItem = config?.appType === "voe" && currentWorkspace?.metadata?.voeColumnName;

  const [selectedCategoryPath, setSelectedCategoryPath] = useState<string>("/");
  const [currentStats, setCurrentStats] = useState<number[]>(
    isYearlyView ? vocCountData.yearlyStats : vocCountData.stats,
  );

  const stats = useMemo(
    () => isYearlyView ? vocCountData.yearlyStats : vocCountData.stats,
    [vocCountData, isYearlyView],
  );

  // 環境名に基づいてComparisonChartコンポーネントを動的に選択
  const ComparisonChartComponent = useMemo(() => {
    return match(config?.env)
      .with("kumiai", () => KumiaiComparisonChart)
      .otherwise(() => ComparisonChart);
  }, [config?.env]);

  // 環境名に基づいてBarChartコンポーネントを動的に選択
  const BarChartComponent = useMemo(() => {
    return match(config?.env)
      .with("kumiai", () => KumiaiCategoryRanking)
      .otherwise(() => BarChart);
  }, [config?.env]);

  useEffect(() => {
    // Workspaceが切り替わってvocCountDataが更新されたらBarChartを更新
    setCurrentStats(stats);
  }, [stats]);

  return (
    <div className={`flex flex-col ${className}`}>
      <Card className="w-full">
        <CardHeader className="px-9">{displayGroup.name}</CardHeader>
        <CardContent className={cn("flex flex-col gap-5 px-0", hideTable ? "pb-7" : "pb-0")}>
          {shouldShowQuestionItem && (
            <div className="min-h-[76px] mx-9 bg-[#fafbff] rounded-[10px] p-4 flex gap-2 items-center shadow-[0px_0px_1.19px_0px_rgba(0,0,0,0.5)]">
              <div className="bg-[#293d72] text-white text-sm font-bold rounded-[5px] h-[34px] px-4 py-2 w-20 text-center shrink-0 flex items-center justify-center">
                質問
              </div>
              <p className="text-[#333333] text-sm font-semibold leading-[1.6]">
                {currentWorkspace?.metadata?.voeColumnName}
              </p>
            </div>
          )}
          <div className="flex mx-9 gap-[10px] justify-start">
            {isYearlyView
              ? (
                <ComparisonChartComponent
                  title={t("bi.chart.title", {
                    month: format(vocCountData.latestYearMonth, t("bi.chart.yearFormat.year")),
                  })}
                  totalCount={vocCountData.thisYearCount ?? 0}
                  changeType={vocCountData.changeTypeFromPrevYear}
                  changeAmount={vocCountData.changeAmountFromPrevYear ?? 0}
                  previousPeriodComparison={vocCountData.yearToYearComparison}
                  comparisonLabelKey="bi.chart.yearlyComparison"
                  className="min-h-[180px] border border-gray-200 rounded-[6px]"
                />
              )
              : (
                <ComparisonChartComponent
                  title={t("bi.chart.title", {
                    month: format(vocCountData.latestYearMonth, t("bi.chart.monthFormat.yearMonthShort")),
                  })}
                  totalCount={vocCountData.thisMonthCount ?? 0}
                  changeType={vocCountData.changeTypeFromPrevMonth}
                  changeAmount={vocCountData.changeAmountFromPrevMonth ?? 0}
                  previousPeriodComparison={vocCountData.monthToMonthComparison}
                  comparisonLabelKey="bi.chart.monthlyComparison"
                  className="min-h-[180px] border border-gray-200 rounded-[6px]"
                />
              )}

            <BarChartComponent
              vocCountData={vocCountData}
              selectedCategoryPath={selectedCategoryPath}
              displayGroup={displayGroup}
              currentStats={currentStats}
              isYearlyView={isYearlyView}
            />
          </div>

          {!hideTable && (
            <BITableContainer
              displayGroup={displayGroup}
              vocCountData={vocCountData}
              selectedCategoryPath={selectedCategoryPath}
              hideDataVisibilityToggle={hideDataVisibilityToggle}
              isYearlyView={isYearlyView}
              setCurrentStats={setCurrentStats}
              setSelectedCategoryPath={setSelectedCategoryPath}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BIContainer;
